// fetchModifiersExist — does this server's channel set hold at least one modifier?
//
// Resume hydration needs one bit (the Review step's "Channel Modifiers"
// panel), not the modifiers. `GET /channel_modifiers/?server_id=` joins on the
// source channel's server, so one request answers for every channel; this used
// to probe each channel in turn.

import fetchFresh, { type Initiate } from "./fetchFresh";

/** What the `getChannelModifiersByServer` endpoint is called with. */
export interface ModifiersByServerArg {
  serverId: string;
  page: number;
  limit: number;
  token: string;
}

/** A modifier row's source channel, in either casing the decoders produce. */
function sourceChannelOf(row: any): unknown {
  return row?.sourceChannelId ?? row?.source_channel_id;
}

/**
 * Resolves true when a modifier sourced from one of `channelIds` exists.
 * `initiate` is `channelModifierAPI.endpoints.getChannelModifiersByServer.initiate`.
 *
 * The server's join does not skip soft-deleted channels, so a row is counted
 * only when its source channel is one the caller still lists; a row that
 * names no source channel is counted. A failed request reads as "none found",
 * as a failed per-channel probe did.
 */
export default async function fetchModifiersExist(
  dispatch: (action: never) => unknown,
  initiate: Initiate<ModifiersByServerArg>,
  serverId: string,
  channelIds: ReadonlySet<string>,
  toId: (value: unknown) => string,
  token: string,
): Promise<boolean> {
  if (channelIds.size === 0) return false;
  try {
    const result: any = await fetchFresh(dispatch, initiate, {
      serverId,
      page: 1,
      limit: 500,
      token,
    });
    const rows = result?.data?.data ?? result?.data ?? result ?? [];
    if (!Array.isArray(rows)) return false;
    return rows.some((row) => {
      const source = sourceChannelOf(row);
      return !source || channelIds.has(toId(source));
    });
  } catch {
    return false;
  }
}
